"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

// ── Step data ──────────────────────────────────────────────────────


const STRINGS: Record<Locale, { steps: { title: string; desc: string[] }[]; title: string; description: string }> = {
  ja: {
    steps: [
      { title: "中身", desc: ["株式か債券か", "全世界か米国か"] },
      { title: "コスト", desc: ["似た商品があれば", "補助的に比べる"] },
      { title: "重なり", desc: ["持っている商品と", "重なっていないか"] },
    ],
    title: "投資信託を選ぶときの順番",
    description: "人気順ではなく、中身 → 必要に応じたコスト比較 → 重なりの順で確認する",
  },
  en: {
    steps: [
      { title: "What is inside", desc: ["Equities or bonds", "Global or US"] },
      { title: "Cost", desc: ["If similar products exist", "compare cost as a secondary check"] },
      { title: "Overlap", desc: ["Whether it overlaps", "with what you already hold"] },
    ],
    title: "The order to use when choosing a fund",
    description: "Not by popularity — check what is inside, then cost where relevant, then overlap",
  },
};

const STEPS = [
  { num: "1", color: COLOR.sky },
  { num: "2", color: COLOR.amber },
  { num: "3", color: COLOR.rose },
] as const;

// ── Illustration ───────────────────────────────────────────────────

function SelectionFlowIllustration() {
  const t = STRINGS[useDocLocale()];
  const vw = 480;
  const vh = 110;
  const stepW = 94;
  const stepH = 76;
  const gap = 20;
  const totalW = STEPS.length * stepW + (STEPS.length - 1) * gap;
  const startX = (vw - totalW) / 2;
  const boxY = (vh - stepH) / 2;

  return (
    <svg viewBox={`0 0 ${vw} ${vh}`} className="w-full">
      {STEPS.map((step, i) => {
        const x = startX + i * (stepW + gap);
        const c = step.color;
        const midY = boxY + stepH / 2;

        return (
          <g key={step.num}>
            {/* Connector arrow */}
            {i > 0 && (
              <g>
                <line
                  x1={x - gap + 2}
                  y1={midY}
                  x2={x - 4}
                  y2={midY}
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeOpacity={0.15}
                />
                <path
                  d={`M ${x - 10} ${midY - 4} L ${x - 3} ${midY} L ${x - 10} ${midY + 4}`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeOpacity={0.15}
                />
              </g>
            )}

            {/* Step box */}
            <rect
              x={x}
              y={boxY}
              width={stepW}
              height={stepH}
              rx={12}
              fill={c}
              fillOpacity={0.07}
              stroke={c}
              strokeWidth="1.2"
            />

            {/* Number + Title */}
            <text x={x + stepW / 2} y={boxY + 24} textAnchor="middle" fontSize="13" fill={c} fontWeight="700">
              {step.num}. {t.steps[i]!.title}
            </text>

            {/* Description */}
            {t.steps[i]!.desc.map((line, li) => (
              <text
                key={li}
                x={x + stepW / 2}
                y={boxY + 40 + li * 13}
                textAnchor="middle"
                fontSize="8"
                fill="currentColor"
              >
                {line}
              </text>
            ))}
          </g>
        );
      })}
    </svg>
  );
}

// ── Export ──────────────────────────────────────────────────────────

export function DocFundSelectionFlowDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard title={t.title} description={t.description}>
      <SelectionFlowIllustration />
    </IllustrationCard>
  );
}
